import type { FC } from "react";
import React, { memo, useId } from "react";

import cn from "classnames";

import type { PopUpTailProps } from "./types";

import classes from "./PopUpTail.styl";
import SideTail from "./assets/SideTail.svg";

const POP_UP_TAIL_TEST_ID = "pop-up-tail-test-id";

/*
 * В Safari был обнаружен баг: в некоторых случаях при первом рендере браузер отрисовывает только первую svg
 * с таким идентификатором в id и clip-path, остальные появляются в DOM, но остаются невидимыми
 * Для решения данной проблемы svg-элементам, имеющим внутри себя взаимодействие по id, приходится
 * генерировать уникальные идентификаторы
 */
const RightLeftTail: FC = () => {
    const id = useId();
    const link = `url(#${id})`;

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="7"
            fill="none"
            data-tsid={RightLeftTail.displayName}
        >
            <g clipPath={link}>
                <path
                    fill="currentColor"
                    fillRule="evenodd"
                    d="M0 6.001c4.917 0 5.95-2.445 7.652-5.37C8.01.013 8.852 0 9 0c.148 0 .99.013 1.348.63C12.05 3.555 13.083 6 18 6V7H0v-.999Z"
                    clipRule="evenodd"
                />
            </g>
            <defs>
                <clipPath id={id}>
                    <path fill="#fff" d="M0 7V0h18v7z" />
                </clipPath>
            </defs>
        </svg>
    );
};

const TopBottomTail: FC = () => {
    const id = useId();
    const link = `url(#${id})`;

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="28"
            height="7"
            fill="none"
            data-tsid={TopBottomTail.displayName}
        >
            <g clipPath={link}>
                <path
                    fill="currentColor"
                    fillRule="evenodd"
                    d="M28 7H0V6C7.649 6 9.256 3.554 11.903.628 12.46.012 13.769 0 14 0c.23 0 1.54.012 2.097.628C18.744 3.554 20.352 6 28 6v1Z"
                    clipRule="evenodd"
                />
            </g>
            <defs>
                <clipPath id={id}>
                    <path fill="#fff" d="M0 7V0h28v7z" />
                </clipPath>
            </defs>
        </svg>
    );
};

/**
 * "Tail" поп-апа. Элемент, присоединяемый к основному телу поп-апа
 */
export const PopUpTail: FC<PopUpTailProps> = memo((props) => {
    const { side, alignment } = props;

    const tailWrapperClasses = cn(classes["tail-wrapper"], {
        [classes[`__placement-${side}-${alignment}`]]: side && alignment,
    });

    const tailClasses = cn(classes.tail, {
        [classes[`__placement-${side}-${alignment}`]]: side && alignment,
    });

    const renderIcon = () => {
        const verticalSides = ["top", "bottom"];
        const horizontalSides = ["left", "right"];
        const centerAlignments = ["center", "custom-start", "custom-end"];

        if (
            centerAlignments.includes(alignment) &&
            horizontalSides.includes(side)
        ) {
            return <RightLeftTail />;
        }

        if (
            centerAlignments.includes(alignment) &&
            verticalSides.includes(side)
        ) {
            return <TopBottomTail />;
        }

        return <SideTail />;
    };

    return (
        <div className={tailWrapperClasses} data-tsid={POP_UP_TAIL_TEST_ID}>
            <div className={tailClasses}>{renderIcon()}</div>
        </div>
    );
});

PopUpTail.displayName = "PopUpTail";
